import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import { Persona } from 'data/crm/Personas'; 
import FeatherIcon from 'feather-icons-react';
import { useNavigate } from 'react-router-dom';

// Creamos un subcomponente interno para envolver el texto clicable de la celda
// Esto nos permite usar el hook 'useNavigate' limpiamente dentro de las columnas
const ClickableCell = ({ persona, children }: { persona: Persona; children: React.ReactNode }) => {
  const navigate = useNavigate();
  return (
    <div 
      className="w-100 h-100 cursor-pointer" 
      onClick={() => navigate(`/app/apps/crm/persona-details/${persona.id}`)}
    >
      {children}
    </div>
  );
};

export const PersonasTableColumns: ColumnDef<Persona>[] = [
  {
    accessorKey: 'id',
    header: () => (
      <div className="d-inline-flex flex-center">
        <div className="px-1 py-1 bg-success-subtle rounded me-2">
          <FeatherIcon icon="file-text" size={16} className="text-success-dark" />
        </div>
        <span>DNI</span>
      </div>
    ),
    // Envolvemos el renderizado de la celda con nuestro componente de navegación
    cell: ({ row: { original: persona } }) => (
      <ClickableCell persona={persona}>
        {persona.id}
      </ClickableCell>
    ),
    meta: {
      headerProps: { style: { width: '25%' }, className: 'ps-4 border-end border-translucent' },
      cellProps: { className: 'white-space-nowrap ps-4 border-end border-translucent fw-semibold text-body-highlight' }
    }
  },
  {
    header: () => (
      <div className="d-inline-flex flex-center">
        <div className="px-1 py-1 bg-info-subtle rounded me-2">
          <FeatherIcon icon="user" size={16} className="text-info-dark" />
        </div>
        <span>Nombre Completo</span>
      </div>
    ),
    id: 'nombre_completo_ui',
    // Generamos el texto combinado y lo hacemos clicable
    cell: ({ row: { original: persona } }) => (
      <ClickableCell persona={persona}>
        {`${persona.apellido}, ${persona.nombres}`}
      </ClickableCell>
    ),
    meta: {
      headerProps: { style: { width: '45%' }, className: 'ps-4 pe-5 border-end border-translucent' },
      cellProps: { className: 'white-space-nowrap ps-4 border-end border-translucent fw-bold text-body-highlight' }
    }
  },
  {
    accessorKey: 'celular',
    header: () => (
      <div className="d-inline-flex flex-center">
        <div className="px-1 py-1 bg-primary-subtle rounded me-2">
          <FeatherIcon icon="phone" size={16} className="text-primary-dark" />
        </div>
        <span>Celular</span>
      </div>
    ),
    cell: ({ row: { original: persona } }) => (
      <ClickableCell persona={persona}>
        {persona.celular || <span className="text-muted">-</span>}
      </ClickableCell>
    ),
    meta: {
      headerProps: { style: { width: '25%', minWidth: '180px' }, className: 'ps-4 pe-5 border-end border-translucent' },
      cellProps: { className: 'white-space-nowrap fw-semibold ps-4 border-end border-translucent' }
    }
  },
  {
    id: 'leadDropdown',
    cell: () => (
      <RevealDropdownTrigger>
        <RevealDropdown>
          <ActionDropdownItems />
        </RevealDropdown>
      </RevealDropdownTrigger>
    ),
    meta: {
      cellProps: { className: 'text-end pe-0 ps-4' }
    }
  }
];

const PersonasTable = () => {
  return (
    <div className="border-top border-translucent">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};

export default PersonasTable;